import PropTypes from 'prop-types';
import React, { createRef, useEffect, useState } from 'react';
import { isEmpty } from 'validate.js';

import Input from 'dpl/common/design_system/Input';
import { ANALYTICS_EVENTS, ANALYTICS_VIEWS } from 'dpl/constants/analytics';
import { TOAST_NOTIFICATIONS_TYPES } from 'dpl/shared/constants/shared';
import useContactContext from 'dpl/contacts/hooks/useContactContext';
import useContactVerification from 'dpl/contacts/hooks/useContactVerification';
import {
  VERIFICATION_CHANNELS,
  VERIFICATION_CHANNEL_NAMES,
  VERIFICATION_CODE_LENGTH
} from 'dpl/contacts/utils/constants';
import useFormContext from 'dpl/shared/form/hooks/useFormContext';
import useToastNotifications from 'dpl/shared/hooks/useToastNotifications';
import { isNumeric } from 'dpl/shared/form/utils';
import { sendAnalyticsEvent } from 'dpl/util/analytics';

const SPECIAL_KEYS = {
  DELETE: 'Delete',
  BACKSPACE: 'Backspace',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight'
};

const INITIAL_INPUT_VALUES = new Array(VERIFICATION_CODE_LENGTH).fill('');
const SPECIAL_KEY_VALUES = Object.values(SPECIAL_KEYS);

const ALT_VERIFICATION_CTAS = {
  [VERIFICATION_CHANNELS.SMS]: 'get verified via email',
  [VERIFICATION_CHANNELS.EMAIL]: 'get verified via phone'
};

function VerificationCodeInput({ value: initialCode, onChange }) {
  const inputRefs = INITIAL_INPUT_VALUES.map(createRef);
  const [inputValues, setInputValues] = useState(INITIAL_INPUT_VALUES);

  const code = inputValues.join('');

  function addInputValue(value, index) {
    setInputValues(
      inputValues.map((inputValue, currentIndex) =>
        currentIndex === index ? value : inputValue
      )
    );
  }

  function removeInputValue(index) {
    setInputValues(
      inputValues.map((inputValue, currentIndex) =>
        currentIndex === index ? '' : inputValue
      )
    );
  }

  function focusInput(index) {
    inputRefs[index]?.current?.focus();
  }

  function handleChange(e, index) {
    const { value } = e.target;

    if (value.length > 1) {
      const pastedValues = [...value]
        .filter(isNumeric)
        .slice(0, VERIFICATION_CODE_LENGTH);

      setInputValues(
        inputValues.map((inputValue, inputIndex) => {
          return pastedValues[inputIndex - index] ?? inputValue;
        })
      );

      focusInput(
        Math.min(index + pastedValues.length, VERIFICATION_CODE_LENGTH - 1)
      );
    } else {
      if (!isNumeric(value)) return;

      addInputValue(value, index);

      if (VERIFICATION_CODE_LENGTH > index) {
        focusInput(index + 1);
      }
    }
  }

  function handleKeyDown(e, index) {
    if (SPECIAL_KEY_VALUES.includes(e.key)) {
      e.preventDefault();
    }

    if (isNumeric(e.key) && isNumeric(inputValues[index])) {
      e.preventDefault();
      addInputValue(e.key, index);
      focusInput(index + 1);
    } else if (e.key === SPECIAL_KEYS.BACKSPACE) {
      removeInputValue(index);
      focusInput(index - 1);
    } else if (e.key === SPECIAL_KEYS.DELETE) {
      removeInputValue(index);
    } else if (e.key === SPECIAL_KEYS.ARROW_LEFT) {
      focusInput(index - 1);
    } else if (e.key === SPECIAL_KEYS.ARROW_RIGHT) {
      focusInput(index + 1);
    }
  }

  useEffect(() => {
    onChange(code);
  }, [code]);

  useEffect(() => {
    // Reset input values when initial code is cleared
    if (isEmpty(initialCode)) {
      setInputValues(INITIAL_INPUT_VALUES);
    }
  }, [initialCode]);

  return (
    <div className="VerificationCodeInput flex justify-center gap-8">
      {inputValues.map((value, index) => (
        <Input
          key={index}
          ref={inputRefs[index]}
          className="tc no-select ph0"
          // Using a tel input as an alternative to number, so that
          // the numeric keypad is shown for mobile users
          type="tel"
          value={value}
          onChange={e => handleChange(e, index)}
          onKeyDown={e => handleKeyDown(e, index)}
          style={{
            width: 40,
            // Hide cursor (transparent color) if input is filled
            caretColor: value ? 'transparent' : null
          }}
        />
      ))}
    </div>
  );
}

VerificationCodeInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string
};

VerificationCodeInput.defaultProps = {
  value: ''
};

export default function VerificationCodeQuestion({
  name,
  phone_number: phoneNumber,
  email,
  isCurrentSection,
  isAnswered
}) {
  const { setValue, formFieldValue, formFieldErrors } = useFormContext(name);
  const { sendCode } = useContactVerification();
  const { eventProps } = useContactContext();
  const { pushToastNotification } = useToastNotifications();

  const channel = formFieldValue?.channel;
  const code = formFieldValue?.code || '';
  const channelName = VERIFICATION_CHANNEL_NAMES[channel];

  function setCode(newCode) {
    setValue(`${name}.code`, newCode);
  }

  function trackButtonClick(e) {
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      ...eventProps,
      view: ANALYTICS_VIEWS.CONTACT_QUIZ,
      button_cta: e.target.textContent
    });
  }

  function handleCodeRequest(e) {
    if (isAnswered) {
      pushToastNotification({
        type: TOAST_NOTIFICATIONS_TYPES.INFO,
        message:
          "You've already answered this one—go ahead to the next question."
      });
      return;
    }

    if (!isEmpty(e?.target)) trackButtonClick(e);

    if (isCurrentSection && formFieldValue?.channel) {
      const { code: _code, ...recipient } = formFieldValue;
      setCode('');
      sendCode(recipient);
    }
  }

  function handleAltChannelPress(e) {
    if (!isEmpty(e?.target)) trackButtonClick(e);

    const altChannelData =
      channel === VERIFICATION_CHANNELS.SMS
        ? { channel: VERIFICATION_CHANNELS.EMAIL, email }
        : { channel: VERIFICATION_CHANNELS.SMS, phone_number: phoneNumber };

    setValue(name, { ...altChannelData, code: '' });
  }

  // Send verification code when section is active or when the channel is changed
  useEffect(() => {
    if (!isCurrentSection || !channel) return;

    handleCodeRequest();
  }, [isCurrentSection, channel]);

  // Clear code input when server returns a verification error
  useEffect(() => {
    if (formFieldErrors?.length) {
      setCode('');
    }
  }, [formFieldErrors]);

  if (!channel) return null;

  return (
    <div className="VerificationCodeQuestion QuestionLayout tc">
      <div className="mb8 mb14-md tc">
        <h3 className="f4 fw-medium f5-md">Verify your {channelName}</h3>
        <p className="mt2 mt4-md font-16 secondary">
          We sent you a verification code to your {channelName}.
        </p>
      </div>
      <VerificationCodeInput value={code} onChange={setCode} />
      <p className="font-16 secondary mt6 tc">
        Didn’t receive a code?{' '}
        <button type="button" className="underline" onClick={handleCodeRequest}>
          Resend code
        </button>
        {phoneNumber && (
          <>
            {' or '}
            <br />
            <button
              type="button"
              className="underline"
              onClick={handleAltChannelPress}
            >
              {ALT_VERIFICATION_CTAS[channel]}
            </button>
          </>
        )}
      </p>
    </div>
  );
}

VerificationCodeQuestion.propTypes = {
  name: PropTypes.string.isRequired,
  phone_number: PropTypes.string,
  email: PropTypes.string.isRequired,
  isCurrentSection: PropTypes.bool.isRequired,
  isAnswered: PropTypes.bool
};

VerificationCodeQuestion.defaultProps = {
  phone_number: null,
  isAnswered: false
};
